import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import LandingPagePremium from "./components/LandingPagePremium";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import CookiePolicy from "./components/CookiePolicy";
import BookingPage from "./pages/BookingPage";
import GatePage from "./pages/GatePage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminShell from "./pages/AdminShell";
import AdminErrorBoundary from "./components/admin/AdminErrorBoundary";
import AdminSupportTickets from "./pages/AdminSupportTickets";
import AdminTimeEntries from "./pages/AdminTimeEntries";
import { AuthProvider } from "./admin/auth/AuthContext";
import StaffLoginPage from "./pages/StaffLoginPage";
import StaffDashboard from "./pages/StaffDashboard";
import StaffLeadsManager from "./pages/StaffLeadsManager";
import StaffShell from "./pages/StaffShell";
import { StaffAuthProvider } from "./context/StaffAuthContext";
import { RoleProvider } from "./context/RoleContext";
import { StaffGuard } from "./components/StaffGuard";
import ClientLogin from "./pages/ClientLogin";
import ClientDashboard from "./pages/ClientDashboard";
import ClientMessaging from "./pages/ClientMessaging";
import ClientOnboarding from "./pages/ClientOnboarding";
import ClientInvoices from "./pages/ClientInvoices";
import ClientProperties from "./pages/ClientProperties";
import ClientAssets from "./pages/ClientAssets";
import ClientSupport from "./pages/ClientSupport";
import ClientPerformance from "./pages/ClientPerformance";
import AdminPerformanceOverview from "./pages/AdminPerformanceOverview";
import StaffMessagingInbox from "./pages/StaffMessagingInbox";
import StaffClients from "./pages/StaffClients";
import StaffPerformance from "./pages/StaffPerformance";
import StaffDashboardNew from "./pages/StaffDashboardNew";
import ClientLeadPortal from "./pages/ClientLeadPortal";
import ClientProfilePage from "./pages/ClientProfilePage";

// Lazy load admin components
const Dashboard = React.lazy(() => import('./components/admin/Dashboard'));
const LeadsManager = React.lazy(() => import('./components/admin/LeadsManager'));
const ClientsManager = React.lazy(() => import('./components/admin/ClientsManager'));
const AdminClientProfile = React.lazy(() => import('./components/admin/AdminClientProfile'));
const HomepageContentEditor = React.lazy(() => import('./components/admin/HomepageContentEditor'));
const CaseStudiesManager = React.lazy(() => import('./components/admin/CaseStudiesManager'));
const HomepageEditor = React.lazy(() => import('./components/admin/HomepageEditor'));
const Settings = React.lazy(() => import('./components/admin/Settings'));
const MessagingInbox = React.lazy(() => import('./components/admin/MessagingInbox'));
const AdminBookingManager = React.lazy(() => import('./pages/AdminBookingManager'));
const AdminSOPManager = React.lazy(() => import('./pages/AdminSOPManager'));
const AdminAnnouncementManager = React.lazy(() => import('./pages/AdminAnnouncementManager'));

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPagePremium />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/book" element={<BookingPage />} />
          
          {/* Admin authentication routes - wrapped in AuthProvider */}
          <Route path="/gate" element={
            <AuthProvider>
              <RoleProvider>
              <GatePage />
            </RoleProvider>
            </AuthProvider>
          } />
          
          <Route path="/admin-panel/login" element={
            <AuthProvider>
              <RoleProvider>
              <AdminLoginPage />
            </RoleProvider>
            </AuthProvider>
          } />
          
          {/* Protected Admin routes - wrapped in AuthProvider, RoleProvider and ErrorBoundary */}
          <Route path="/admin-panel" element={
            <AuthProvider>
              <RoleProvider>
              <RoleProvider>
                <AdminErrorBoundary>
                  <AdminShell>
                    <Navigate to="/admin-panel/dashboard" replace />
                  </AdminShell>
                </AdminErrorBoundary>
              </RoleProvider>
            </RoleProvider>
            </AuthProvider>
          } />
          
          <Route path="/admin-panel/dashboard" element={
            <AuthProvider>
              <RoleProvider>
              <RoleProvider>
                <AdminErrorBoundary>
                  <AdminShell>
                    <React.Suspense fallback={
                      <div className="flex items-center justify-center p-8">
                        <div className="text-gray-600">Loading dashboard...</div>
                      </div>
                    }>
                      <Dashboard />
                    </React.Suspense>
                  </AdminShell>
                </AdminErrorBoundary>
              </RoleProvider>
            </RoleProvider>
            </AuthProvider>
          } />
          
          <Route path="/admin-panel/leads" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-gray-600">Loading leads...</div>
                    </div>
                  }>
                    <LeadsManager />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          <Route path="/admin-panel/clients" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-gray-600">Loading clients...</div>
                    </div>
                  }>
                    <ClientsManager />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          {/* Admin Client Profile Page */}
          <Route path="/admin-panel/clients/:clientId" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-gray-600">Loading client profile...</div>
                    </div>
                  }>
                    <AdminClientProfile />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          
          <Route path="/admin-panel/homepage-content" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-gray-600">Loading homepage content editor...</div>
                    </div>
                  }>
                    <HomepageContentEditor />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />
          
          <Route path="/admin-panel/case-studies" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-white">Loading case studies...</div>
                    </div>
                  }>
                    <CaseStudiesManager />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />
          
          <Route path="/admin-panel/settings" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-white">Loading settings...</div>
                    </div>
                  }>
                    <Settings />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          <Route path="/admin-panel/messaging" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-white">Loading messaging...</div>
                    </div>
                  }>
                    <MessagingInbox />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          <Route path="/admin-panel/performance" element={
            <AuthProvider>
              <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center p-8">
                      <div className="text-white">Loading performance...</div>
                    </div>
                  }>
                    <AdminPerformanceOverview />
                  </React.Suspense>
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          {/* Admin Support Tickets */}
          <Route path="/admin-panel/support-tickets" element={
            <AuthProvider>
            <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <AdminSupportTickets />
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          {/* Admin Time Entries */}
          <Route path="/admin-panel/time-entries" element={
            <AuthProvider>
            <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <AdminTimeEntries />
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          {/* Admin Booking Manager */}
          <Route path="/admin-panel/bookings" element={
            <AuthProvider>
            <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <AdminBookingManager />
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />

          {/* Admin SOP Manager */}
          <Route path="/admin-panel/sops" element={
            <AuthProvider>
            <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <AdminSOPManager />
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />
          
          <Route path="/admin-panel/announcements" element={
            <AuthProvider>
            <RoleProvider>
              <AdminErrorBoundary>
                <AdminShell>
                  <AdminAnnouncementManager />
                </AdminShell>
              </AdminErrorBoundary>
            </RoleProvider>
            </AuthProvider>
          } />
          
          {/* Fallback for admin routes */}
          <Route path="/admin-panel/*" element={<Navigate to="/admin-panel/dashboard" replace />} />
          
          {/* Backward compatibility - redirect old /admin to new /admin-panel */}
          <Route path="/admin/*" element={<Navigate to="/admin-panel/dashboard" replace />} />
          
          {/* Client Lead Portal - lightweight dashboard for contractors */}
          <Route path="/portal" element={<ClientLeadPortal />} />
          <Route path="/portal/login" element={<ClientLogin portalMode={true} />} />
          
          {/* Legacy client routes - redirect to portal */}
          <Route path="/client/login" element={<Navigate to="/portal/login" replace />} />
          <Route path="/client-dashboard" element={<ClientDashboard />} />
          <Route path="/client-dashboard/messages" element={<ClientMessaging />} />
          <Route path="/client-dashboard/onboarding" element={<ClientOnboarding />} />
          <Route path="/client-dashboard/invoices" element={<ClientInvoices />} />
          <Route path="/client-dashboard/properties" element={<ClientProperties />} />
          <Route path="/client-dashboard/assets" element={<ClientAssets />} />
          <Route path="/client-dashboard/support" element={<ClientSupport />} />
          <Route path="/client-dashboard/performance" element={<ClientPerformance />} />

          {/* Staff routes - separate authentication system */}
          <Route path="/staff-login" element={
            <StaffAuthProvider>
              <StaffLoginPage />
            </StaffAuthProvider>
          } />
          
          {/* Legacy redirect */}
          <Route path="/staff/login" element={<Navigate to="/staff-login" replace />} />
          <Route path="/staff" element={<Navigate to="/staff-d" replace />} />
          <Route path="/staff/*" element={<Navigate to="/staff-d" replace />} />
          
          {/* New Staff Dashboard */}
          <Route path="/staff-d" element={
            <StaffAuthProvider>
              <RoleProvider>
                <StaffDashboardNew />
              </RoleProvider>
            </StaffAuthProvider>
          } />
          
          {/* Client Profile Full Page */}
          <Route path="/staff-d/client/:clientId" element={
            <StaffAuthProvider>
              <RoleProvider>
                <ClientProfilePage />
              </RoleProvider>
            </StaffAuthProvider>
          } />
          
          <Route path="/staff-d/*" element={
            <StaffAuthProvider>
              <RoleProvider>
                <StaffDashboardNew />
              </RoleProvider>
            </StaffAuthProvider>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;